// src/store/slices/inequalitiesSlice.ts
/**
 * Inequalities Redux Slice
 * 
 * Manages inequality regions on the coordinate grid.
 * Phase 7: Vectors and Inequalities
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import { Inequality, FeasibleRegion, InequalityType, PlotEquation, ParsedEquation } from '../../types/coordinateGrid';
import { parseInequality } from '../../lib/coordinateGrid/equationParser';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface InequalitiesState {
  inequalities: Inequality[];
  feasibleRegions: FeasibleRegion[];
  selectedInequalityIds: string[];
  nextId: number;
  /** Default styling for new inequalities */
  defaultStyle: {
    fillColor: string;
    fillOpacity: number;
    boundaryStyle: 'solid' | 'dashed';
  };
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: InequalitiesState = {
  inequalities: [],
  feasibleRegions: [],
  selectedInequalityIds: [],
  nextId: 1,
  defaultStyle: {
    fillColor: '#3B82F6', // Blue
    fillOpacity: 0.3,
    boundaryStyle: 'dashed',
  },
};

// Color palette for auto-assigning inequality colors
const INEQUALITY_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a PlotEquation from a parsed equation for the boundary
 */
function createBoundaryEquation(parsed: { equation: ParsedEquation; type: InequalityType; isValid: boolean }): PlotEquation {
  return {
    id: `boundary-${Date.now()}`,
    expression: parsed.equation.expression,
    type: parsed.equation.type,
    coefficients: parsed.equation.coefficients,
    color: '#374151', // Dark gray for boundary
    thickness: 2,
    visible: true,
    showLabel: false,
    showIntercepts: false,
    showGradient: false,
    gradientTriangles: [],
    isSelected: false,
  };
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const inequalitiesSlice = createSlice({
  name: 'inequalities',
  initialState,
  reducers: {
    /**
     * Add a new inequality
     */
    addInequality: (
      state,
      action: PayloadAction<{
        expression: string;
        style?: Partial<Inequality>;
      }>
    ) => {
      const parsed = parseInequality(action.payload.expression);
      if (!parsed.isValid) return;

      const colorIndex = state.inequalities.length % INEQUALITY_COLORS.length;
      const boundaryEquation = createBoundaryEquation(parsed);

      const newInequality: Inequality = {
        id: `ineq-${state.nextId}`,
        expression: action.payload.expression,
        type: parsed.type,
        boundaryEquation,
        fillColor: INEQUALITY_COLORS[colorIndex],
        fillOpacity: state.defaultStyle.fillOpacity,
        boundaryStyle: parsed.type === '<=' || parsed.type === '>=' ? 'solid' : 'dashed',
        visible: true,
        isSelected: false,
        ...action.payload.style,
      };

      state.inequalities.push(newInequality);
      state.nextId++;
    },

    /**
     * Update an existing inequality
     */
    updateInequality: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<Inequality>;
      }>
    ) => {
      const inequality = state.inequalities.find((i) => i.id === action.payload.id);
      if (inequality) {
        Object.assign(inequality, action.payload.updates);
        
        // Re-parse if expression changed
        if (action.payload.updates.expression) {
          const parsed = parseInequality(action.payload.updates.expression);
          if (parsed.isValid) {
            inequality.type = parsed.type;
            inequality.boundaryEquation = createBoundaryEquation(parsed);
            inequality.boundaryStyle = parsed.type === '<=' || parsed.type === '>=' ? 'solid' : 'dashed';
          }
        }
      }
    },

    /**
     * Delete an inequality
     */
    deleteInequality: (state, action: PayloadAction<string>) => {
      state.inequalities = state.inequalities.filter((i) => i.id !== action.payload);
      state.selectedInequalityIds = state.selectedInequalityIds.filter((id) => id !== action.payload);
      
      // Remove feasible regions that reference this inequality
      state.feasibleRegions = state.feasibleRegions.filter(
        (region) => !region.inequalityIds.includes(action.payload)
      );
    },

    /**
     * Select an inequality (with optional multi-select via Ctrl key)
     */
    selectInequality: (
      state,
      action: PayloadAction<{
        id: string;
        ctrlKey?: boolean;
      }>
    ) => {
      const { id, ctrlKey = false } = action.payload;

      if (!ctrlKey) {
        // Deselect all inequalities
        state.inequalities.forEach((i) => {
          i.isSelected = false;
        });
        state.selectedInequalityIds = [];
      }

      const inequality = state.inequalities.find((i) => i.id === id);
      if (inequality) {
        inequality.isSelected = !inequality.isSelected;
        if (inequality.isSelected) {
          if (!state.selectedInequalityIds.includes(id)) {
            state.selectedInequalityIds.push(id);
          }
        } else {
          state.selectedInequalityIds = state.selectedInequalityIds.filter((iid) => iid !== id);
        }
      }
    },

    /**
     * Deselect all inequalities
     */
    deselectAllInequalities: (state) => {
      state.inequalities.forEach((i) => {
        i.isSelected = false;
      });
      state.selectedInequalityIds = [];
    },

    /**
     * Toggle visibility of an inequality
     */
    toggleVisibility: (state, action: PayloadAction<string>) => {
      const inequality = state.inequalities.find((i) => i.id === action.payload);
      if (inequality) {
        inequality.visible = !inequality.visible;
      }
    },

    /**
     * Highlight feasible region (intersection of multiple inequalities)
     */
    highlightFeasibleRegion: (
      state,
      action: PayloadAction<{
        inequalityIds: string[];
        highlightColor?: string;
      }>
    ) => {
      const { inequalityIds, highlightColor = '#F59E0B' } = action.payload;

      // Remove existing feasible regions with the same inequality IDs
      state.feasibleRegions = state.feasibleRegions.filter(
        (region) => JSON.stringify(region.inequalityIds.sort()) !== JSON.stringify(inequalityIds.sort())
      );

      const newRegion: FeasibleRegion = {
        inequalityIds,
        highlightColor,
      };

      state.feasibleRegions.push(newRegion);
    },

    /**
     * Clear feasible region
     */
    clearFeasibleRegion: (state, action: PayloadAction<string[]>) => {
      const inequalityIds = action.payload;
      state.feasibleRegions = state.feasibleRegions.filter(
        (region) => JSON.stringify(region.inequalityIds.sort()) !== JSON.stringify(inequalityIds.sort())
      );
    },

    /**
     * Clear all feasible regions
     */
    clearAllFeasibleRegions: (state) => {
      state.feasibleRegions = [];
    },

    /**
     * Load inequalities state (for scene loading)
     */
    loadInequalitiesState: (state, action: PayloadAction<Partial<InequalitiesState>>) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all inequalities
     */
    clearAllInequalities: (state) => {
      state.inequalities = [];
      state.feasibleRegions = [];
      state.selectedInequalityIds = [];
    },
  },
});

// Export actions
export const {
  addInequality,
  updateInequality,
  deleteInequality,
  selectInequality,
  deselectAllInequalities,
  toggleVisibility,
  highlightFeasibleRegion,
  clearFeasibleRegion,
  clearAllFeasibleRegions,
  loadInequalitiesState,
  clearAllInequalities,
} = inequalitiesSlice.actions;

// Selectors
export const selectAllInequalities = (state: RootState) => state.inequalities.inequalities;
export const selectVisibleInequalities = (state: RootState) =>
  state.inequalities.inequalities.filter((i) => i.visible);
export const selectSelectedInequalities = (state: RootState) =>
  state.inequalities.inequalities.filter((i) => i.isSelected);
export const selectFeasibleRegions = (state: RootState) => state.inequalities.feasibleRegions;

export default inequalitiesSlice.reducer;
